import { useGetOffersCargo } from "@/entities/get-offers-cargo/fetch";
import { usePutOffersCargo } from "@/entities/put-offers-cargo/fetch";
import { useGlobalNotification } from "@/entities/set-global-notification/fetch";
import { useSuspendOffersProcess } from "@/entities/suspend-offers-process/fetch";
import { useInProgressValuesLocalCache } from "@/features/offer-cargo-create-in-progress-values-local-cache";
import { fetchOfferCargoProcess } from "@/features/offer-cargo-process/fetch";
import type { AdminCargoCreatePageProps } from "@/pages/admin-cargo-create/model";
import { isSuccessStatusCode } from "@/shared/lib/http";
import { safeParseInt } from "@/shared/lib/number";
import { replaceNullWithUndefined } from "@/shared/lib/object";
import { useNavigate } from "@tanstack/react-router";

import { useCallback, useMemo, useState } from "react";
import type { AdminCargoDetailPageProps, DraftOfferCargo } from "../model";

const concatAddress = (draftOfferCargo: DraftOfferCargo) => {
  const destinations = draftOfferCargo.destinations.map((destination) => ({
    ...destination,
    city: destination.city,
    addressLine: destination.addressLine,
    address: destination.addressLine
      ? `${destination.city} ${destination.addressLine}`
      : destination.city,
  }));
  return { ...draftOfferCargo, destinations };
};

export const useAdminCargoDetailPageProps = (
  cargoIdStr?: string,
): AdminCargoDetailPageProps => {
  const cargoId = useMemo(() => {
    if (!cargoIdStr) {
      return undefined;
    }
    const id = safeParseInt(cargoIdStr);
    if (!id) {
      return undefined;
    }
    return id;
  }, [cargoIdStr]);

  if (!cargoId) {
    throw new Error("cargoId is required");
  }

  // State
  const [previewValues, setPreviewValues] =
    useState<AdminCargoDetailPageProps["previewValues"]>(null);

  // Local Storage
  // 記入途中の情報をローカルストレージに保存する
  const [inProgressValues, setInProgressValues, removeInProgressValues] =
    useInProgressValuesLocalCache<
      AdminCargoDetailPageProps["defaultInputValues"]
    >("AdminCargoDetailPage");

  // コピーして新規作成する際に、元になる荷物情報を受け渡すためにLocalStorageに保存する
  const [_, setAdminCargoCreateInProgressValues] =
    useInProgressValuesLocalCache<
      AdminCargoCreatePageProps["defaultInputValues"]
    >("AdminCargoCreatePage");

  // Backend API
  const getOffersCargo = useGetOffersCargo(
    {
      id: cargoId,
    },
    {
      enabled: !!cargoId,
    },
  );

  const putOffersCargo = usePutOffersCargo();
  const suspendOffersProcess = useSuspendOffersProcess();

  // Global Notification
  const { setGlobalNotification } = useGlobalNotification();

  // Navigation
  const navigate = useNavigate();

  // Props
  const { offerCargoProcess } = fetchOfferCargoProcess(
    getOffersCargo.data?.data.process.id,
  );

  // Actions

  const onInputValuesUpdate = useCallback<
    AdminCargoDetailPageProps["onInputValuesUpdate"]
  >(
    (draftOfferCargo) => {
      setInProgressValues(draftOfferCargo);
    },
    [setInProgressValues],
  );

  const onConfirmClick = useCallback<
    AdminCargoDetailPageProps["onConfirmClick"]
  >(async (draftOfferCargo) => {
    setPreviewValues(draftOfferCargo);
  }, []);

  const onBackToInProgressClick = useCallback<
    AdminCargoDetailPageProps["onBackToInProgressClick"]
  >(() => {
    setPreviewValues(null);
  }, []);

  const onSubmitClick = useCallback<AdminCargoDetailPageProps["onSubmitClick"]>(
    async (draftOfferCargo) => {
      if (!cargoId) return;

      console.log("Submitting cargo:", draftOfferCargo.companyId);

      const response = await putOffersCargo.mutateAsync({
        id: cargoId,
        data: {
          ...concatAddress(draftOfferCargo),
          vehicleId: null,
        },
      });
      if (response && isSuccessStatusCode(response.status)) {
        setGlobalNotification({
          variant: "success",
          message: "荷物の更新が完了しました",
          autoHide: true,
        });
        removeInProgressValues();
        setPreviewValues(null);
        // データを再取得
        getOffersCargo.refetch();
      }
    },
    [
      cargoId,
      putOffersCargo.mutateAsync,
      removeInProgressValues,
      setGlobalNotification,
      getOffersCargo,
    ],
  );

  const onProcessSuspend = useCallback<
    AdminCargoDetailPageProps["onProcessSuspend"]
  >(async () => {
    if (!getOffersCargo.data?.data.process.id) return;

    try {
      const response = await suspendOffersProcess.mutateAsync({
        id: getOffersCargo.data.data.process.id,
      });
      if (response && isSuccessStatusCode(response.status)) {
        setGlobalNotification({
          variant: "success",
          message: "配送提案の受付を停止しました",
          autoHide: true,
        });
        // データを再取得
        getOffersCargo.refetch();
      }
    } catch {
      setGlobalNotification({
        variant: "error",
        message: "受付停止に失敗しました",
        autoHide: true,
      });
    }
  }, [
    getOffersCargo.data?.data.process.id,
    suspendOffersProcess.mutateAsync,
    setGlobalNotification,
    getOffersCargo,
  ]);

  const onCopyCargoClick = useCallback(() => {
    if (offerCargoProcess.status !== "success") {
      return;
    }
    setAdminCargoCreateInProgressValues(
      replaceNullWithUndefined({
        ...offerCargoProcess.data.draftOfferCargo,
        // 積み日/卸し日/受付期限は空欄とする
        loadingOn: undefined,
        unloadingOn: undefined,
        deadlineAt: undefined,
        destinations: offerCargoProcess.data.draftOfferCargo.destinations.map(
          (d) => {
            const [city, address_line] = d.address.split(" ");
            return { ...d, city, address_line };
          },
        ),
        // workaround: vehicleWeight が string で帰ってくるので変換する
        vehicleWeight:
          safeParseInt(offerCargoProcess.data.draftOfferCargo.vehicleWeight) ??
          undefined,
      }),
    );
    navigate({
      to: "/admin/cargos/create",
    });
  }, [
    navigate,
    setAdminCargoCreateInProgressValues,
    offerCargoProcess.status,
    offerCargoProcess.data,
  ]);

  return {
    cargoId,
    offerCargoProcess,
    defaultInputValues: inProgressValues,
    previewValues,
    onInputValuesUpdate,
    onBackToInProgressClick,
    onSubmitClick,
    onConfirmClick,
    onProcessSuspend,
    onCopyCargoClick,
  };
};
